// src/utils/pricingUtils.js
// Pazaryeri Fiyatlandırma ve Kâr Hesaplama Utility Fonksiyonları

/**
 * Varsayılan pazaryeri parametreleri
 */
export const DEFAULT_MARKETPLACE_PARAMS = {
  trendyol: {
    id: 'trendyol',
    name: 'Trendyol',
    icon: '🟠',
    color: '#FF6B00',
    commissionRate: 0.20,      // %20
    fixedFee: 0,               // Sabit hizmet bedeli
    shippingType: 'free',      // free | paid | fixed
    shippingCost: 29.99,       // Kargo ücreti (satıcıya yansıyan)
    adSpendRate: 0.10,         // %10 reklam
    paymentDelay: 14,          // Ödeme kesinti süresi (gün)
    vatRate: 0.20,             // %20 KDV
    currency: 'TRY',
    returnRate: 0.05,          // %5 iade oranı
  },
  hepsiburada: {
    id: 'hepsiburada',
    name: 'Hepsiburada',
    icon: '🟡',
    color: '#FF8C00',
    commissionRate: 0.18,
    fixedFee: 2.99,
    shippingType: 'free',
    shippingCost: 34.99,
    adSpendRate: 0.08,
    paymentDelay: 21,
    vatRate: 0.20,
    currency: 'TRY',
    returnRate: 0.04,
  },
  amazon: {
    id: 'amazon',
    name: 'Amazon',
    icon: '📦',
    color: '#FF9900',
    commissionRate: 0.15,
    fixedFee: 0,
    shippingType: 'paid',
    shippingCost: 39.99,
    adSpendRate: 0.12,
    paymentDelay: 14,
    vatRate: 0.20,
    currency: 'TRY',
    returnRate: 0.06,
  },
  shopify: {
    id: 'shopify',
    name: 'Shopify',
    icon: '🟢',
    color: '#96BF48',
    commissionRate: 0.029,     // %2.9 ödeme komisyonu
    fixedFee: 0.30,            // $0.30 sabit (TRY karşılığı)
    shippingType: 'paid',
    shippingCost: 0,
    adSpendRate: 0.15,
    paymentDelay: 3,
    vatRate: 0.20,
    currency: 'TRY',
    returnRate: 0.03,
  }
};

/**
 * KDV dahil fiyattan KDV hariç fiyat hesapla
 * @param {number} priceWithVat - KDV dahil fiyat
 * @param {number} vatRate - KDV oranı (0.20 = %20)
 * @returns {number} KDV hariç fiyat
 */
export function priceExcludingVat(priceWithVat, vatRate = 0.20) {
  return priceWithVat / (1 + vatRate);
}

/**
 * KDV hariç fiyattan KDV dahil fiyat hesapla
 * @param {number} priceWithoutVat - KDV hariç fiyat
 * @param {number} vatRate - KDV oranı
 * @returns {number} KDV dahil fiyat
 */
export function priceIncludingVat(priceWithoutVat, vatRate = 0.20) {
  return priceWithoutVat * (1 + vatRate);
}

/**
 * Net kâr hesapla (satış fiyatı verildiğinde)
 * Pazaryeri net kâr hesapla
 * 
 * @param {object} params
 * @property {number} params.salePrice - Satış Fiyatı (KDV Dahil)
 * @property {number} params.rawMaterialCost - Ürün Hammadde Maliyeti
 * @property {number} params.laborCost - İşçilik Maliyeti
 * @property {number} params.packagingCost - Paketleme Maliyeti
 * @property {number} params.subcontractingCost - Fason Üretim Maliyeti
 * @property {number} params.shippingCost - Gidiş Kargo Maliyeti
 * @property {number} params.commissionRate - Komisyon Oranı (0.15 = %15)
 * @property {number} params.fixedFee - Sabit Hizmet Bedeli
 * @property {number} params.adSpendRate - Reklam Oranı (0.10 = %10)
 * @property {number} params.adSpendFixed - Sabit Reklam Gideri
 * @property {number} params.returnRate - İade Oranı (0.05 = %5)
 * @property {number} [params.returnShippingCost] - İade Geliş Kargo Maliyeti
 * @property {number} params.vatRate - Kdv Oranı (0.10 = %10)
 * @property {number} params.productVatRate - Ürün Alış Kdv Oranı (0.20 = %20)
 * @returns {object} Detaylı hesaplama sonuçları
 */
export function calculateNetProfit(params) {
  const {
    salePrice = 0,
    rawMaterialCost = 0,
    laborCost = 0,
    packagingCost = 0,
    shippingCost = 0,
    commissionRate = 0,
    fixedFee = 0,
    adSpendRate = 0,
    adSpendFixed = 0,
    returnRate = 0,
    returnShippingCost = 0,
    vatRate = 0.20,
    productVatRate = 0.20,
    subcontractingCost = 0,
    exchangeRate = 1,
    customCosts = 0,
    productVatIncluded = true,
    shippingVatIncluded = true,
    withholdingRate = 0,
  } = params;

  const SERVICE_VAT_RATE = 0.20;

  // 1. Ürün Maliyeti (KDV Dahil / Hariç Analizi)
  const baseProductCost = (rawMaterialCost + laborCost + packagingCost + subcontractingCost + customCosts) * exchangeRate;
  const productCostExVat = productVatIncluded ? (baseProductCost / (1 + productVatRate)) : baseProductCost;
  const productVat = productVatIncluded ? (baseProductCost - productCostExVat) : (baseProductCost * productVatRate);
  const totalProductCost = productCostExVat + productVat; // Brüt Maliyet

  // 2. Satış ve Hesaplanan KDV (Ödenecek)
  const priceExVat = priceExcludingVat(salePrice, vatRate);
  const salesVat = salePrice - priceExVat;

  // 3. Komisyon (KDV DAHİL satış fiyatı üzerinden kesilir ve %20 KDV İçerir)
  const commissionAmount = (salePrice * commissionRate) + fixedFee;
  const commissionExVat = commissionAmount / (1 + SERVICE_VAT_RATE);
  const commissionVat = commissionAmount - commissionExVat;

  // 4. Reklam (KDV DAHİL satış üzerinden)
  const adCost = adSpendFixed > 0 ? adSpendFixed : (salePrice * adSpendRate);
  const adExVat = adCost / (1 + SERVICE_VAT_RATE);
  const adVat = adCost - adExVat;

  // 5. Kargo
  const shippingExpense = shippingVatIncluded ? shippingCost : priceIncludingVat(shippingCost, SERVICE_VAT_RATE);
  const shippingExVat = shippingExpense / (1 + SERVICE_VAT_RATE);
  const shippingVat = shippingExpense - shippingExVat;

  // 6. Stopaj (Satış fiyatı üzerinden net kesinti, KDV içermez)
  const withholdingAmount = salePrice * withholdingRate;

  // 7. İade Etkisi (Sadece Gidiş ve Geliş Kargo Maliyetleri üzerinden alınır - Kullanıcı isteği)
  // İade durumunda giden kargo yanar + dönüş kargosu ödenir.
  const returnImpact = (shippingExVat + (returnShippingCost / (1 + SERVICE_VAT_RATE))) * returnRate;

  // 8. Net Ödenecek KDV (KDV Beyannamesi Mantığı)
  const totalRecoverableVat = productVat + commissionVat + adVat + shippingVat;
  const netVatToPay = salesVat - totalRecoverableVat; // Negatif çıkarsa "Devreden KDV"  
  // 9. Net Kâr (Satış - Tüm Brüt Maliyetler - Ödenecek KDV)
  const netProfit = salePrice - totalProductCost - commissionAmount - shippingExpense - adCost - withholdingAmount - netVatToPay - returnImpact;

  // Kâr marjı (%)
  const profitMargin = priceExVat > 0 ? (netProfit / priceExVat) * 100 : 0;

  // ROI (Yatırım Getirisi %)
  const totalInvestmentExVat = productCostExVat + shippingExVat;
  const roi = totalInvestmentExVat > 0 ? (netProfit / totalInvestmentExVat) * 100 : 0;

  return {
    salePrice: round2(salePrice),
    priceExVat: round2(priceExVat),
    vatAmount: round2(salesVat),
    productCostExVat: round2(productCostExVat),
    commissionExVat: round2(commissionExVat),
    shippingExVat: round2(shippingExVat),
    adExVat: round2(adExVat),
    totalProductCost: round2(totalProductCost),
    commissionAmount: round2(commissionAmount),
    shippingExpense: round2(shippingExpense),
    adCost: round2(adCost),
    productVat: round2(productVat),
    productVatRate: productVatRate,
    vatRate: vatRate,
    commissionVat: round2(commissionVat),
    shippingVat: round2(shippingVat),
    adVat: round2(adVat),
    totalRecoverableVat: round2(totalRecoverableVat),
    netVatToPay: round2(netVatToPay),
    withholdingAmount: round2(withholdingAmount),
    returnImpact: round2(returnImpact),
    netProfit: round2(netProfit),
    profitMargin: round2(profitMargin),
    roi: round2(roi),
    isProfitable: netProfit > 0,
    breakdown: {
      rawMaterialCost: round2(rawMaterialCost * exchangeRate),
      laborCost: round2(laborCost * exchangeRate),
      packagingCost: round2(packagingCost * exchangeRate),
      subcontractingCost: round2(subcontractingCost * exchangeRate),
      customCosts: round2(customCosts * exchangeRate),
    }
  };
}

/**
 * Hedef kâr marjından satış fiyatı hesapla
 * 
 * Formül (yaklaşık):
 *   Net Maliyet = Toplam Maliyet + Kargo + İade Etkisi
 *   KDV Hariç Fiyat = Net Maliyet / (1 − Komisyon Oranı − Reklam Oranı − Hedef Kâr Marjı/100)
 *   Satış Fiyatı = KDV Hariç Fiyat × (1 + KDV)
 * 
 * @param {object} params
 * @returns {object} Hesaplanan satış fiyatı ve detaylar
 */
export function calculateSalePrice(params) {
  const {
    targetProfitMargin = 20,
    rawMaterialCost = 0,
    laborCost = 0,
    packagingCost = 0,
    shippingCost = 0,
    commissionRate = 0,
    fixedFee = 0,
    adSpendRate = 0,
    adSpendFixed = 0,
    returnRate = 0,
    vatRate = 0.20,
    productVatRate = 0.20,
    subcontractingCost = 0,
    exchangeRate = 1,
    customCosts = 0,
    productVatIncluded = true,
    shippingVatIncluded = true,
    withholdingRate = 0,
    returnShippingCost = 0,
  } = params;

  const SERVICE_VAT_RATE = 0.20;

  const baseProductCost = (rawMaterialCost + laborCost + packagingCost + subcontractingCost + customCosts) * exchangeRate;
  const productCostExVat = productVatIncluded ? (baseProductCost / (1 + productVatRate)) : baseProductCost;
  
  const shippingExVat = shippingVatIncluded ? (shippingCost / (1 + SERVICE_VAT_RATE)) : shippingCost;
  const returnImpact = (shippingExVat + (returnShippingCost / (1 + SERVICE_VAT_RATE))) * returnRate;
  const fixedFeeExVat = fixedFee / (1 + SERVICE_VAT_RATE);

  const netCostExVat = productCostExVat + shippingExVat + returnImpact + fixedFeeExVat;

  const targetMarginDecimal = targetProfitMargin / 100;
  const withholdingImpact = (1 + vatRate) * withholdingRate;
  const commissionImpact = ((1 + vatRate) * commissionRate) / (1 + SERVICE_VAT_RATE);
  const adImpact = ((1 + vatRate) * adSpendRate) / (1 + SERVICE_VAT_RATE);

  const divisor = 1 - commissionImpact - adImpact - withholdingImpact - targetMarginDecimal;

  if (divisor <= 0) {
    return {
      error: true,
      message: 'Hedef kâr marjı, komisyon ve reklam oranları toplamı %100\'ü aşıyor. Lütfen değerleri kontrol edin.',
      salePrice: 0,
      priceExVat: 0,
    };
  }

  let priceExVat;
  if (adSpendFixed > 0) {
    const divisorFixed = 1 - commissionImpact - withholdingImpact - targetMarginDecimal;
    if (divisorFixed <= 0) {
      return {
        error: true,
        message: 'Hedef kâr marjı ve komisyon oranı toplamı %100\'ü aşıyor.',
        salePrice: 0,
        priceExVat: 0,
      };
    }
    const adFixedExVat = adSpendFixed / (1 + SERVICE_VAT_RATE);
    priceExVat = (netCostExVat + adFixedExVat) / divisorFixed;
  } else {
    priceExVat = netCostExVat / divisor;
  }

  const salePrice = priceExVat * (1 + vatRate);

  // Doğrulama: Hesaplanan fiyatla detaylı net kâr tablosunu oluştur
  const netProfitResult = calculateNetProfit({
    salePrice,
    rawMaterialCost,
    laborCost,
    packagingCost,
    shippingCost,
    commissionRate,
    fixedFee,
    adSpendRate,
    adSpendFixed,
    returnRate,
    returnShippingCost,
    vatRate,
    productVatRate,
    subcontractingCost,
    exchangeRate,
    customCosts,
    productVatIncluded,
    shippingVatIncluded,
    withholdingRate,
  });

  return {
    ...netProfitResult,
    targetProfitMargin,
    calculatedSalePrice: round2(salePrice),
    calculatedPriceExVat: round2(priceExVat),
  };
}

/**
 * Break-even (başa-baş) fiyat hesapla — net kâr = 0
 */
export function calculateBreakEven(params) {
  return calculateSalePrice({
    ...params,
    targetProfitMargin: 0,
  });
}

/**
 * Birden fazla pazaryerinde karşılaştırma yap
 * @param {object} productCosts - Ürün maliyet bilgileri
 * @param {object} marketplaces - Pazaryeri parametreleri (key: id, value: params)
 * @param {number} salePrice - (Opsiyonel) Sabit satış fiyatı
 * @param {number} targetProfitMargin - (Opsiyonel) Hedef kâr marjı
 * @returns {object} Pazaryeri karşılaştırma sonuçları
 */
export function compareMarketplaces(productCosts, marketplaces, salePrice = null, targetProfitMargin = null) {
  const results = {};

  Object.entries(marketplaces).forEach(([key, mp]) => {
    const calcParams = {
      ...productCosts,
      commissionRate: mp.commissionRate,
      fixedFee: mp.fixedFee || 0,
      adSpendRate: mp.adSpendRate || 0,
      returnRate: mp.returnRate || productCosts.returnRate || 0,
      vatRate: mp.vatRate || productCosts.vatRate || 0.20,
      productVatRate: productCosts.productVatRate !== undefined ? productCosts.productVatRate : 0.20,
      shippingCost: mp.shippingCost || productCosts.shippingCost || 0,
      productVatIncluded: productCosts.productVatIncluded !== undefined ? productCosts.productVatIncluded : true,
      shippingVatIncluded: productCosts.shippingVatIncluded !== undefined ? productCosts.shippingVatIncluded : true,
      withholdingRate: productCosts.withholdingRate || 0,
    };

    if (salePrice) {
      // Fiyat verildi → kâr hesapla
      results[key] = {
        ...calculateNetProfit({ ...calcParams, salePrice }),
        marketplace: mp,
        mode: 'profit',
      };
    } else if (targetProfitMargin !== null) {
      // Hedef kâr verildi → fiyat hesapla
      results[key] = {
        ...calculateSalePrice({ ...calcParams, targetProfitMargin }),
        marketplace: mp,
        mode: 'price',
      };
    }

    // Break-even her durumda hesapla
    const breakEven = calculateBreakEven(calcParams);
    results[key].breakEvenPrice = breakEven.calculatedSalePrice || breakEven.salePrice;
  });

  // En kârlı pazaryerini belirle
  const entries = Object.entries(results);
  if (entries.length > 0) {
    if (salePrice) {
      // En yüksek net kâr
      const best = entries.reduce((a, b) => 
        (a[1].netProfit || 0) > (b[1].netProfit || 0) ? a : b
      );
      results._bestMarketplace = best[0];
    } else {
      // En düşük satış fiyatı (aynı kâr marjında en uygun fiyat)
      const best = entries.filter(([k]) => !k.startsWith('_')).reduce((a, b) =>
        (a[1].calculatedSalePrice || Infinity) < (b[1].calculatedSalePrice || Infinity) ? a : b
      );
      results._bestMarketplace = best[0];
    }
  }

  return results;
}

/**
 * Toplu ürün hesaplama (Excel verisi için)
 * @param {Array} products - Ürün listesi [{name, costs, salePrice?, targetMargin?}]
 * @param {object} marketplace - Pazaryeri parametreleri
 * @returns {Array} Hesaplanmış ürün listesi
 */
export function bulkCalculate(products, marketplace) {
  return products.map(product => {
    const calcParams = {
      rawMaterialCost: product.rawMaterialCost || product.costs?.rawMaterial || 0,
      laborCost: product.laborCost || product.costs?.labor || 0,
      packagingCost: product.packagingCost || product.costs?.packaging || 0,
      shippingCost: marketplace.shippingCost || product.shippingCost || 0,
      commissionRate: marketplace.commissionRate,
      fixedFee: marketplace.fixedFee || 0,
      adSpendRate: marketplace.adSpendRate || 0,
      adSpendFixed: marketplace.adSpendFixed || 0,
      returnRate: marketplace.returnRate || product.returnRate || 0,
      vatRate: marketplace.vatRate || product.vatRate || 0.20,
      productVatRate: product.productVatRate !== undefined ? product.productVatRate : 0.20,
      subcontractingCost: product.subcontractingCost || 0,
      exchangeRate: product.exchangeRate || 1,
      productVatIncluded: product.productVatIncluded !== undefined ? product.productVatIncluded : true,
      shippingVatIncluded: product.shippingVatIncluded !== undefined ? product.shippingVatIncluded : true,
      withholdingRate: product.withholdingRate || 0,
    };

    let result;
    if (product.salePrice) {
      result = calculateNetProfit({ ...calcParams, salePrice: product.salePrice });
    } else if (product.targetProfitMargin) {
      result = calculateSalePrice({ ...calcParams, targetProfitMargin: product.targetProfitMargin });
    } else {
      result = calculateNetProfit({ ...calcParams, salePrice: 0 });
    }

    return {
      ...product,
      ...result,
      productName: product.name || product.productName || 'İsimsiz Ürün',
    };
  });
}

/**
 * Kampanya fiyatlandırma hesaplama
 * İki kampanya tipi desteklenir:
 *   1. buyXpayY  — "N al M öde" (ör. 4 al 3 öde)
 *   2. percentDiscount — "% indirim + pazaryeri katkısı"
 *
 * @param {object} params
 * @property {'buyXpayY'|'percentDiscount'} params.campaignType
 * @property {number} params.salePrice          — Liste satış fiyatı (KDV dahil)
 * @property {number} [params.buyQuantity]       — Kaç adet al (buyXpayY)
 * @property {number} [params.payQuantity]       — Kaç adet öde (buyXpayY)
 * @property {number} [params.discountRate]      — Toplam indirim % (percentDiscount)
 * @property {number} [params.marketplaceContribution] — Pazaryeri katkı % (percentDiscount)
 * @property {number} [params.estimatedSales]    — Kampanya dönemi tahmini adet satış
 * @property {number} ...costParams              — calculateNetProfit'e aktarılan tüm parametreler
 * @returns {object} Kampanyalı ve kampanyasız karşılaştırma sonuçları
 */
export function calculateCampaignPricing(params) {
  const {
    campaignType = 'buyXpayY',
    salePrice = 0,
    buyQuantity = 4,
    payQuantity = 3,
    discountRate = 10,
    marketplaceContribution = 2,
    estimatedSales = 100,
    ...costParams
  } = params;

  // --- Ortak maliyet parametreleri (calculateNetProfit'e gönderilecek) ---
  const baseCalcParams = { ...costParams };

  // 1. Kampanyasız birim hesaplama
  const normalResult = calculateNetProfit({ ...baseCalcParams, salePrice });

  let campaignResult;
  let campaignLabel = '';
  let effectiveUnitPrice = salePrice;
  let sellerDiscountPct = 0;
  let marketplaceDiscountPct = 0;

  if (campaignType === 'buyXpayY') {
    // ── N Al M Öde ──
    // Müşteri buyQuantity adet alır, payQuantity kadar öder.
    // Pazaryeri komisyonu efektif birim fiyat üzerinden kesilir.
    effectiveUnitPrice = round2((salePrice * payQuantity) / buyQuantity);
    sellerDiscountPct = round2(((salePrice - effectiveUnitPrice) / salePrice) * 100);

    campaignResult = calculateNetProfit({ ...baseCalcParams, salePrice: effectiveUnitPrice });
    campaignLabel = `${buyQuantity} Al ${payQuantity} Öde`;
  } else {
    // ── Yüzde İndirim (Pazaryeri Karşılamalı) ──
    // Toplam indirim = discountRate %
    // Pazaryeri katkısı = marketplaceContribution % (komisyondan düşülür)
    // Satıcının üstlendiği indirim = discountRate - marketplaceContribution
    const totalDiscount = discountRate;
    marketplaceDiscountPct = Math.min(marketplaceContribution, totalDiscount);
    sellerDiscountPct = round2(totalDiscount - marketplaceDiscountPct);

    // Satıcının efektif fiyatı = Satış Fiyatı × (1 − satıcı indirim %)
    effectiveUnitPrice = round2(salePrice * (1 - sellerDiscountPct / 100));

    // Pazaryeri katkısını komisyon oranından düşür
    const adjustedCommissionRate = Math.max(
      (baseCalcParams.commissionRate || 0) - (marketplaceDiscountPct / 100),
      0
    );

    campaignResult = calculateNetProfit({
      ...baseCalcParams,
      salePrice: effectiveUnitPrice,
      commissionRate: adjustedCommissionRate,
    });
    campaignLabel = `%${totalDiscount} İndirim (%${marketplaceDiscountPct} PY Karşılamalı)`;
  }

  // 2. Toplam etki hesaplama (tahmini satış hacmine göre)
  const normalTotalProfit = round2(normalResult.netProfit * estimatedSales);
  const campaignTotalProfit = round2(campaignResult.netProfit * estimatedSales);
  const profitDifference = round2(campaignResult.netProfit - normalResult.netProfit);
  const totalProfitDifference = round2(campaignTotalProfit - normalTotalProfit);

  return {
    campaignType,
    campaignLabel,
    effectiveUnitPrice: round2(effectiveUnitPrice),
    sellerDiscountPct,
    marketplaceDiscountPct,
    estimatedSales,

    // Birim bazında sonuçlar
    normal: normalResult,
    campaign: campaignResult,

    // Karşılaştırma
    unitProfitDifference: profitDifference,
    normalTotalProfit,
    campaignTotalProfit,
    totalProfitDifference,

    // Kampanya değerli mi?
    isCampaignProfitable: campaignResult.netProfit > 0,
    isWorthIt: campaignTotalProfit > 0, // Zarar etmiyor mu?
  };
}

// --- Yardımcı Fonksiyonlar ---

function round2(num) {
  return Math.round((num + Number.EPSILON) * 100) / 100;
}

export function formatCurrency(amount, currency = 'TRY') {
  return new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount || 0);
}

export function formatPercent(value) {
  return `%${(value || 0).toFixed(2)}`;
}

export function getMarketplaceList() {
  return Object.values(DEFAULT_MARKETPLACE_PARAMS);
}

export function getMarketplaceById(id) {
  return DEFAULT_MARKETPLACE_PARAMS[id] || null;
}
